import ptBR from "./pt-BR.js";
import type { PlannerContent } from "./types.js";

const content: PlannerContent = {
  meta: {
    ...ptBR.meta,
    title: "Kuori para cerimonialistas",
    description:
      "Os convidados fotografam pelo QR code, sem app, e as fotos vão para o álbum do evento e para o telão ao vivo, com a marca do seu cerimonial. Por evento, sem mensalidade, com nota fiscal.",
    whatsapp: "Oi! Sou cerimonialista e vim pela página da Kuori.",
  },
  nav: { ...ptBR.nav, home: "/", demo: "Demonstração" },
  hero: {
    kicker: "Para cerimonialistas",
    title: ["Você cuida do evento.", "As fotos dos convidados ", "chegam sozinhas."],
    sub: "Os convidados fotografam pelo QR code, sem instalar nada, e as fotos vão para o álbum do evento e para o telão ao vivo, com a marca do seu cerimonial.",
    cta: "Falar no WhatsApp",
    alt: "Testar a demonstração",
    altHref: "/cerimonialistas/demo/",
    slides: [
      "/assets/photos/hero-danca.webp",
      "/assets/photos/hero-jardim.webp",
      "/assets/photos/hero-petalas.webp",
      "/assets/photos/hero-primeira-danca.webp",
    ],
  },
  pain: {
    label: "Depois do evento",
    title: "Quem junta as fotos dos convidados?",
    body: "Hoje elas ficam espalhadas em grupos de WhatsApp, em stories que somem em 24 horas e em arquivos comprimidos. O cliente pede, e você corre atrás. Com a Kuori, cada foto já nasce no álbum do evento.",
  },
  how: {
    label: "Na sua operação",
    title: "Antes, durante e depois.",
    steps: [
      [
        "Antes",
        "Crie o evento do cliente",
        "Nome, data, número de convidados e a sua marca. O QR sai pronto para o convite, o cartão de mesa e o telão.",
      ],
      [
        "No dia",
        "O QR faz o trabalho",
        "Quem aponta a câmera entra na hora, pelo navegador, até de dentro do WhatsApp. Ninguém precisa de você para participar.",
      ],
      [
        "Depois",
        "Entregue o álbum",
        "As fotos chegam separadas por hora, moderadas e em qualidade original, prontas para o cliente.",
      ],
    ],
  },
  operations: {
    label: "No dia",
    title: "Nada para você operar.",
    body: "O telão roda sozinho numa TV ou num projetor. Cada convidado tem uma cota de fotos, e nada aparece no telão sem passar pela revelação. Se precisar, você oculta uma foto pelo celular, em segundos.",
    bullets: [
      "Telão em tela cheia, sem operador",
      "Cota de fotos por pessoa",
      "Revelação na hora ou no fim",
      "Moderação pelo celular",
    ],
  },
  photographer: {
    label: "E o fotógrafo?",
    title: "O fotógrafo registra o evento. Os convidados registram o que ele não vê.",
    body: "A Kuori não substitui a cobertura profissional. Ela junta os ângulos da pista, das mesas e dos bastidores que só quem estava lá viu.",
  },
  organizer: {
    label: "Seu cerimonial",
    title: "Todos os seus clientes num painel.",
    body: "Casamento, 15 anos, formatura e evento corporativo, lado a lado. Cada um com o seu convite, o seu álbum, a sua fila de moderação e o seu telão.",
    bullets: [
      "Painel multi-evento",
      "Convite com QR e link curto",
      "Moderação e banimento",
      "Revelação na hora ou no fim",
    ],
  },
  panel: {
    ...ptBR.panel,
    label:
      "Ilustração do painel do organizador: a lista de eventos com um casamento ao vivo, dois eventos agendados e um evento de 15 anos encerrado",
    events: {
      ...ptBR.panel.events,
      birthday: {
        name: "15 anos · Helena",
        meta: "sáb, 22 ago · 120 convidados · 874 mídias",
        short: "874 mídias",
      },
    },
  },
  brand: {
    label: "Marca branca",
    title: "O cliente vê o seu nome, não o nosso.",
    body: "Nome, logotipo e cor do seu cerimonial no convite, no álbum e no telão, incluídos em todo evento. A interface é neutra de propósito: a cor da tela vem das fotos do evento.",
  },
  pricing: {
    ...ptBR.pricing,
    title: "Por evento. Sem mensalidade.",
    sub: "Escolha a faixa pelo número de convidados. Com nota fiscal, e dá para subir de faixa depois pagando só a diferença. Você decide se inclui no seu pacote ou cobra à parte.",
    tracks: [["empresarial", "Empresarial"]],
    trackNote: {
      empresarial: "Autosserviço até 750 convidados, com nota fiscal e a sua marca incluída.",
    },
    includes: {
      free: ptBR.pricing.includes.free,
      paid: ["Marca branca do seu cerimonial", ...ptBR.pricing.includes.paid],
    },
    contract: {
      title: "Faz muitos eventos por ano?",
      body: "Montamos um contrato para o volume do seu cerimonial, com condições combinadas com você.",
      cta: "Conversar sobre contrato",
      message: "Oi! Sou cerimonialista e quero conversar sobre um contrato para vários eventos.",
    },
  },
  faq: {
    label: "Perguntas",
    title: "O que perguntam antes de fechar.",
    items: [
      [
        "Meu cliente vê a marca Kuori?",
        "No convite, no álbum e no telão aparecem o nome, o logotipo e a cor do seu cerimonial. A Kuori fica só numa assinatura discreta.",
      ],
      [
        "O fotógrafo vai se incomodar?",
        "A Kuori junta as fotos dos convidados e não substitui a cobertura profissional. A entrega principal continua sendo a do fotógrafo.",
      ],
      [
        "Quem cuida das fotos no dia?",
        "Ninguém precisa operar nada. Se quiser, você oculta uma foto ou bane um autor pelo celular, a qualquer momento.",
      ],
      [
        "O convidado precisa instalar alguma coisa?",
        "Não. Ele aponta a câmera para o QR code e a câmera do evento abre no navegador, até de dentro do WhatsApp e do Instagram.",
      ],
      [
        "E se vierem mais convidados do que o previsto?",
        "Você sobe de faixa e paga só a diferença. O evento continua o mesmo, com o mesmo QR e o mesmo álbum.",
      ],
      ["Tem nota fiscal?", "Tem. Todo evento pago sai com nota fiscal."],
      [
        "Por quanto tempo o álbum fica disponível?",
        "Em qualquer faixa paga, 12 meses de acesso e mais 12 meses para exportar os arquivos.",
      ],
      [
        "Posso testar antes de oferecer ao cliente?",
        "Pode. A demonstração monta um evento de exemplo com a sua marca em dois minutos, direto no navegador. E a faixa de até 10 convidados é grátis.",
      ],
    ],
  },
  foot: {
    cta: "Vamos testar no seu próximo evento?",
    action: "Falar no WhatsApp",
    demo: "Testar a demonstração",
    note: "Respondemos no mesmo dia útil.",
    rights: "2026 Kuori",
  },
};

export default content;
